import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Typography,
  LinearProgress,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Checkbox,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { animate, stagger } from "motion";
import getApiBase from "../components/getApiBase";
import PrivacyPolicy from "../assets/Autoshops_Privacy_Policy.pdf";
import TermsOfService from "../assets/Autoshops_Terms_and_Conditions.pdf";
import TurnstileWidget from "./TurnstileWidget";
import { useLocation, useSearchParams } from "react-router-dom";
import { usStates } from "../data/states";
import { Link as RouterLink } from "react-router-dom";
import { Radio, Chip } from "@mui/material";

const MechanicSignupForm = ({
  onSuccess,
  onError,
  onFormStart,
  onFormBackToStart,
  inviteToken,
  inviteEmail,
  joiningShopId,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    role: inviteToken ? "Mechanic" : "Owner", // Auto-set role based on invite
    shopName: "",
    shopAddress: "",
    city: "",
    state: "",
    shopZipcode: "",
    shopPhone: "",
    shopSite: "",
    joiningShopId: "",
    selectedPlaceId: "", // Track selected shop by Google place_id for UI
    inviteToken: inviteToken || "",
    manuallyEntered: false, // Track if shop was manually entered
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [availableShops, setAvailableShops] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Track if search was done
  const [isBroadSearching, setIsBroadSearching] = useState(false); // Loading state (reused for unified search)
  const [allowManualEntry, setAllowManualEntry] = useState(false); // Allow manual entry after search fails
  const [showOwnershipAcknowledgement, setShowOwnershipAcknowledgement] =
    useState(false); // Upfront ownership acknowledgement modal
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submissions

  // New state for invite token (co-owner requests)
  const [inviteTokenFromURL, setInviteTokenFromURL] = useState(null);
  const [inviteShop, setInviteShop] = useState(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);

  const [searchParams] = useSearchParams();

  // Field validation states
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldTouched, setFieldTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Step state for multi-step form
  const [step, setStep] = useState(0);

  // Show ownership acknowledgement modal when page loads (only for non-invite users)
  useEffect(() => {
    if (!inviteToken) {
      setShowOwnershipAcknowledgement(true);
    }
  }, [inviteToken]);

  // Update formData when inviteToken or inviteEmail props change
  useEffect(() => {
    console.log(
      "🔍 MechanicSignupForm - inviteToken prop:",
      inviteToken,
      "inviteEmail prop:",
      inviteEmail
    );
    if (inviteToken) {
      console.log("✅ Setting formData.inviteToken to:", inviteToken);
      setFormData((prev) => ({
        ...prev,
        inviteToken: inviteToken,
        email: inviteEmail || prev.email, // Pre-fill email from invite
        role: "Mechanic", // Force mechanic role for invites
      }));
    }
  }, [inviteToken, inviteEmail]);

  // Check for invite token from URL params and verify it
  useEffect(() => {
    const token = searchParams.get("invite_token");
    const mechanicInvite = searchParams.get("invite") || inviteToken;

    if (token) {
      setInviteTokenFromURL(token);
      verifyInviteToken(token);
    } else if (mechanicInvite) {
      verifyMechanicInvite(mechanicInvite);
    }
  }, [searchParams, inviteToken]);

  const verifyInviteToken = async (token) => {
    setIsLoadingInvite(true);

    try {
      const response = await fetch(
        `${getApiBase()}/api/owner-join-requests/verify-invite/${token}`,
        { credentials: "include" }
      );

      const data = await response.json();

      if (data.success) {
        setInviteShop(data.shop);

        // Pre-fill email from invite
        setFormData((prev) => ({
          ...prev,
          email: data.requester_email || prev.email,
          firstName: data.requester_first_name || prev.firstName,
          lastName: data.requester_last_name || prev.lastName,
          inviteToken: token,
          role: "Mechanic",
        }));

        // Show success message
        onError(
          `You've been invited to join ${data.shop.shop_name}!`,
          "success"
        );
      } else {
        onError(
          "Invalid or expired invite link. You can still register normally.",
          "warning"
        );
        setInviteTokenFromURL(null);
      }
    } catch (error) {
      console.error("Error verifying invite:", error);
      onError("Error verifying invite link", "error");
    } finally {
      setIsLoadingInvite(false);
    }
  };

  const verifyMechanicInvite = async (token) => {
    setIsLoadingInvite(true);
    try {
      const response = await fetch(
        `${getApiBase()}/api/invite/info/${encodeURIComponent(token)}`,
      );
      const data = await response.json();
      if (!response.ok) {
        onError(data.error || "Invalid or expired invite link.", "warning");
        return;
      }
      setInviteShop(data.shop);
      setFormData((prev) => ({
        ...prev,
        email: data.email || prev.email,
        inviteToken: token,
        joiningShopId: String(data.shopId || prev.joiningShopId),
        joiningExistingShop: true,
        role: "Mechanic",
      }));
    } catch (error) {
      console.error("Error verifying mechanic invite:", error);
      onError("Error verifying invite link", "error");
    } finally {
      setIsLoadingInvite(false);
    }
  };

  const handleTurnstileVerify = (token) => {
    setTurnstileToken(token);
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const evaluatePassword = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[!@#$%^&*()_\-+=]/.test(password)) strength += 25; // Fixed: Match backend exactly
    if (/[A-Z]/.test(password)) strength += 25;
    return strength;
  };

  // Helper function to validate domain names (user-friendly)
  const isValidDomain = (string) => {
    if (!string.trim()) return true; // Empty is valid (optional field)

    // Remove protocol if present for validation
    let domain = string.replace(/^https?:\/\//, "").replace(/^www\./, "");

    // Basic domain pattern: must have at least one dot and valid characters
    const domainPattern =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

    return domainPattern.test(domain);
  };

  // Helper function to normalize URL for database storage
  const normalizeUrl = (url) => {
    if (!url.trim()) return "";

    // If it already has a protocol, return as-is
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Add https:// prefix
    return `https://${url}`;
  };

  // Comprehensive field validation for mechanic form
  const validateField = (name, value) => {
    const errors = {};

    switch (name) {
      case "firstName":
      case "lastName":
        if (!value.trim()) {
          errors[name] = `${
            name === "firstName" ? "First" : "Last"
          } name is required`;
        } else if (value.length > 50) {
          errors[name] = `${
            name === "firstName" ? "First" : "Last"
          } name must be 50 characters or less`;
        } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          errors[name] = `${
            name === "firstName" ? "First" : "Last"
          } name can only contain letters, spaces, hyphens, and apostrophes`;
        }
        break;

      case "email":
        if (!value.trim()) {
          errors[name] = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors[name] = "Please enter a valid email address";
        }
        break;

      case "phone":
        if (!value.trim()) {
          errors[name] = "Phone number is required";
        } else if (!/^\+?1?\d{10,14}$/.test(value.replace(/[\s\-\(\)]/g, ""))) {
          errors[name] = "Please enter a valid phone number";
        }
        break;

      case "password":
        const passwordErrors = [];
        if (value.length < 8) passwordErrors.push("at least 8 characters");
        if (!/[0-9]/.test(value)) passwordErrors.push("one number");
        if (!/[A-Z]/.test(value)) passwordErrors.push("one uppercase letter");
        if (!/[!@#$%^&*()_\-+=]/.test(value))
          passwordErrors.push(
            "one special character (! @ # $ % ^ & * ( ) _ - + =)"
          );

        if (passwordErrors.length > 0) {
          errors[name] = `Password must contain: ${passwordErrors.join(", ")}`;
        }
        break;

      case "shopName":
        if (!formData.joiningExistingShop && !value.trim()) {
          errors[name] = "Shop name is required";
        }
        break;

      case "shopAddress":
        if (!formData.joiningExistingShop && !value.trim()) {
          errors[name] = "Shop address is required";
        }
        break;

      case "city":
        if (!formData.joiningExistingShop && !value.trim()) {
          errors[name] = "City is required";
        }
        break;

      case "state":
        if (!formData.joiningExistingShop && !value.trim()) {
          errors[name] = "State is required";
        }
        break;

      case "shopZipcode":
        if (!formData.joiningExistingShop && !value.trim()) {
          errors[name] = "ZIP code is required";
        } else if (
          !formData.joiningExistingShop &&
          !/^\d{5}(-\d{4})?$/.test(value)
        ) {
          errors[name] = "Please enter a valid ZIP code";
        }
        break;

      case "shopPhone":
        if (!formData.joiningExistingShop && !value.trim()) {
          errors[name] = "Shop phone number is required";
        } else if (
          !formData.joiningExistingShop &&
          !/^\+?1?\d{10,14}$/.test(value.replace(/[\s\-\(\)]/g, ""))
        ) {
          errors[name] = "Please enter a valid shop phone number";
        }
        break;

      case "shopSite":
        if (value.trim() && !isValidDomain(value)) {
          errors[name] =
            "Please enter a valid website (e.g., yourshop.com or www.yourshop.com)";
        }
        break;

      default:
        break;
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "inviteToken") return; // only block token override, if needed
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") setPasswordStrength(evaluatePassword(value));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Helper function to format phone number
  const formatPhoneNumber = (phoneNumber) => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, "");

    // Check if it's a valid US phone number length
    if (cleaned.length === 10) {
      // Format as (XXX) XXX-XXXX
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
      // Format as +1 (XXX) XXX-XXXX
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(
        4,
        7
      )}-${cleaned.slice(7)}`;
    }

    // Return original if not a standard format
    return phoneNumber;
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    setFieldTouched((prev) => ({ ...prev, [name]: true }));

    // Auto-format phone numbers
    if ((name === "phone" || name === "shopPhone") && value.trim()) {
      const formattedPhone = formatPhoneNumber(value);
      if (formattedPhone !== value) {
        setFormData((prev) => ({ ...prev, [name]: formattedPhone }));
      }
    }

    // Check email availability when user leaves email field
    if (name === "email" && value.trim()) {
      const normalizedEmail = value.toLowerCase().trim();

      // Only check if email format is valid
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
        try {
          const response = await fetch(
            `${getApiBase()}/api/auth/check-email?email=${encodeURIComponent(
              normalizedEmail
            )}`,
            {
              method: "GET",
              credentials: "include",
            }
          );

          const data = await response.json();

          if (!data.available) {
            // Email is already registered
            setFieldErrors((prev) => ({
              ...prev,
              email: data.message || "This email is already registered",
            }));
          } else {
            // Email is available - clear any previous email error
            setFieldErrors((prev) => {
              const newErrors = { ...prev };
              if (
                newErrors.email &&
                newErrors.email.includes("already registered")
              ) {
                delete newErrors.email;
              }
              return newErrors;
            });
          }
        } catch (error) {
          console.error("Error checking email availability:", error);
          // Don't block form submission if check fails - backend will catch it
        }
      }
    }

    const fieldValidationErrors = validateField(name, value);
    setFieldErrors((prev) => ({ ...prev, ...fieldValidationErrors }));
  };

  // Check if form is valid
  const checkFormValidity = () => {
    // Step 0: Personal info (firstName, lastName, email, phone, password)
    if (step === 0) {
      const requiredFields = [
        "firstName",
        "lastName",
        "email",
        "phone",
        "password",
      ];
      const hasAllRequiredFields = requiredFields.every((field) =>
        formData[field]?.trim()
      );
      const hasNoErrors = Object.keys(fieldErrors).length === 0;
      const hasStrongPassword = passwordStrength === 100;

      return hasAllRequiredFields && hasNoErrors && hasStrongPassword;
    }

    // For other steps, check all fields including captcha for final submission
    const requiredFields = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "password",
    ];

    // Add shop-specific required fields if creating new shop
    if (!formData.joiningExistingShop) {
      requiredFields.push(
        "shopName",
        "shopAddress",
        "city",
        "state",
        "shopZipcode",
        "shopPhone"
      );
    }

    const hasAllRequiredFields = requiredFields.every((field) =>
      formData[field]?.trim()
    );
    const hasNoErrors = Object.keys(fieldErrors).length === 0;
    const hasStrongPassword = passwordStrength === 100;

    // Turnstile required on final step when site key is configured
    const hasTurnstile = !turnstileSiteKey || Boolean(turnstileToken);
    return (
      hasAllRequiredFields && hasNoErrors && hasStrongPassword && agreedToTerms && hasTurnstile
    );
  };

  // Update form validity when dependencies change
  React.useEffect(() => {
    setIsFormValid(checkFormValidity());
  }, [formData, fieldErrors, passwordStrength, agreedToTerms, step, turnstileToken]);

  const handleSearchShops = async () => {
    console.log(
      "🔍 Adam's New Search: Name:",
      formData.shopName,
      "ZIP:",
      formData.shopZipcode
    );

    if (!formData.shopZipcode || formData.shopZipcode.length !== 5) {
      console.warn("⚠️ ZIP code (5 digits) required");
      return;
    }

    // Show loading state
    setIsBroadSearching(true);
    setHasSearched(false);
    setAvailableShops([]);

    try {
      // Shop name is optional - pass empty string if not provided
      const shopNameParam = formData.shopName?.trim() || "";
      const apiUrl = `${getApiBase()}/api/autoshops/mechanic-signup-search?name=${encodeURIComponent(
        shopNameParam
      )}&zip=${formData.shopZipcode}`;
      console.log("📡 Calling Adam's 4-step search:", apiUrl);

      const res = await fetch(apiUrl);
      console.log("📥 Response status:", res.status, res.statusText);

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ API error:", errorText);
        setIsBroadSearching(false);
        setHasSearched(true);
        return;
      }

      const data = await res.json();
      console.log(
        "✅ Search complete! Step:",
        data.step,
        "Method:",
        data.method,
        "Found:",
        data.shops?.length || 0
      );

      // Stop loading
      setIsBroadSearching(false);
      setHasSearched(true);

      if (data.success && data.shops && data.shops.length > 0) {
        console.log("📋 Shops found:", data.shops);
        // Format shops for display (ensure consistent structure)
        const formattedShops = data.shops.map((shop) => ({
          id: shop.id,
          shop_name: shop.shop_name,
          address: shop.address,
          phone_number: shop.phone_number,
          url: shop.url,
          rating: shop.rating,
          place_id: shop.place_id,
          lat: shop.lat,
          lng: shop.lng,
          in_network: shop.in_network,
          distance_miles: shop.distance_miles,
        }));
        setAvailableShops(formattedShops);
      } else {
        console.log("⚠️ No shops found - showing manual entry option");
        setAvailableShops([]);
      }
    } catch (err) {
      console.error("❌ Failed to search shops:", err);
      setIsBroadSearching(false);
      setHasSearched(true);
      setAvailableShops([]);
    }
  };

  const handleClaimShop = (shop) => {
    const addressParts = shop.address?.split(",") || [];
    const shopStreet = addressParts[0]?.trim() || "";
    const city = addressParts[1]?.trim() || "";
    const stateZip = addressParts[2]?.trim() || "";
    const [state, zip] = stateZip.split(" ");

    console.log("🏪 Claiming shop:", {
      id: shop.id,
      place_id: shop.place_id,
      name: shop.shop_name,
      phone: shop.phone_number,
    });

    setFormData((prev) => ({
      ...prev,
      joiningShopId: shop.id, // Use database ID for backend (may be undefined for new shops)
      selectedPlaceId: shop.place_id, // Use place_id for UI selection (always unique)
      shopName: shop.shop_name || "",
      shopAddress: shopStreet,
      city: city || prev.city,
      state: state || prev.state,
      shopZipcode: zip || prev.shopZipcode,
      shopPhone: shop.phone_number || prev.shopPhone || "", // Use shop phone if available
      shopSite: shop.url || "",
    }));
  };

  // Co-owner request handler
  const handleRequestCoOwnership = async (shop) => {
    const confirmed = window.confirm(
      `This shop is already claimed. Would you like to request to join "${shop.shop_name}" as a co-owner?\n\nThe existing owner will be notified and can approve or reject your request.`
    );

    if (!confirmed) return;

    // Collect requester info from form
    if (!formData.email || !formData.firstName || !formData.lastName) {
      onError("Please fill in your name and email first", "error");
      return;
    }

    try {
      const response = await fetch(
        `${getApiBase()}/api/owner-join-requests/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            shop_id: shop.id,
            requester_email: formData.email,
            requester_first_name: formData.firstName,
            requester_last_name: formData.lastName,
            requester_phone: formData.phone || null,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        onError(
          "Request submitted successfully! The existing owner will be notified and can approve your request. You will receive an email with further instructions.",
          "success"
        );
        // Clear search after successful request
        setHasSearched(false);
        setAvailableShops([]);
      } else {
        onError("Error: " + data.error, "error");
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      onError("Failed to submit request", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submissions
    if (isSubmitting) {
      console.log(
        "⚠️ Signup already in progress, ignoring duplicate submission"
      );
      return;
    }

    if (passwordStrength < 100) {
      onError("Password must meet all requirements!", "error");
      return;
    }

    // Proceed with signup (ownership was acknowledged upfront)
    await proceedWithSignup();
  };

  const proceedWithSignup = async () => {
    // Prevent double submissions
    if (isSubmitting) {
      console.log(
        "⚠️ Signup already in progress, ignoring duplicate submission"
      );
      return;
    }

    setIsSubmitting(true);
    console.log("🚀 Starting signup process for:", formData.email);

    try {
      if (turnstileSiteKey && !turnstileToken) {
        onError("Please complete the bot verification challenge.", "error");
        setIsSubmitting(false);
        return;
      }

      const payload = {
        ...formData,
        placeId: formData.selectedPlaceId,
        turnstileToken,
        shopSite: normalizeUrl(formData.shopSite),
      };

      // Handle invite token (from URL or props)
      const activeInviteToken = inviteTokenFromURL || formData.inviteToken;
      if (activeInviteToken) {
        payload.is_active = false;
        payload.role = "Mechanic";
        payload.invite_token = activeInviteToken;
      }

      // Use shop_id from invite or from shop selection
      const activeShopId = formData.joiningShopId;
      if (activeShopId) {
        payload.shop_id = activeShopId;
        payload.joiningShopId = activeShopId;
      }

      console.log("📤 Sending signup request to backend...");
      console.log("🔍 DEBUG: Payload before sending:", JSON.stringify(payload, null, 2));
      const response = await fetch(`${getApiBase()}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("📥 Signup response:", {
        status: response.status,
        ok: response.ok,
        data,
      });

      if (response.ok) {
        console.log("✅ Signup successful!");
        // Check if email verification is required (Mechanics/Owners)
        if (data.requiresEmailVerification) {
          onSuccess({
            requiresEmailVerification: true,
            email: data.email,
            message:
              data.message ||
              "Account created! Please check your email to verify your account before logging in.",
          });
        } else {
          // Auto-login for other roles (shouldn't happen for mechanics, but kept for safety)
          if (data.token) {
            localStorage.setItem("token", data.token);
          }
          if (data.user) {
            localStorage.setItem("user", JSON.stringify(data.user));
          }
          onSuccess(data.user);
        }
      } else {
        console.error("❌ Signup failed:", data.error);
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        onError(data.error || "Signup failed!", "error");
      }
    } catch (err) {
      console.error("❌ Signup error:", err);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      onError("Server error! Try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOwnershipAcknowledge = () => {
    setShowOwnershipAcknowledgement(false);
  };

  const handleOwnershipDecline = () => {
    setShowOwnershipAcknowledgement(false);
    // Redirect back to home or login
    window.location.href = "/";
  };

  // FUNCTIONS TO CONTROL NEXT/BACK BUTTONS with the toggle
  function next() {
    if (step === 0 && onFormStart) {
      onFormStart();
    }
    setStep(step + 1);
  }

  function prev() {
    const newStep = step - 1;
    if (newStep === 0 && onFormBackToStart) {
      onFormBackToStart();
    }
    setStep(newStep);
  }
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromURL =
      searchParams.get("token") ||
      searchParams.get("invite") ||
      inviteToken ||
      "";
    const shopIdFromURL = searchParams.get("shopId") || joiningShopId || "";

    setFormData((prev) => ({
      ...prev,
      inviteToken: tokenFromURL || prev.inviteToken,
      role: tokenFromURL || inviteToken ? "Mechanic" : prev.role,
      joiningShopId: shopIdFromURL || prev.joiningShopId,
      joiningExistingShop: !!(tokenFromURL || shopIdFromURL),
    }));
  }, [location]);

  // ANIMATIONS
  useEffect(() => {
    const userInputs = Array.from(document.getElementsByClassName("userInput"));
    if (userInputs.length > 0) {
      animate(
        userInputs,
        { x: [-30, 0], opacity: [0, 1] },
        { delay: stagger(0.3) }
      );
    }
  }, [step]);

  const inputArray = [
    <>
      <TextField
        className="userInput"
        label="First Name"
        name="firstName"
        fullWidth
        required
        value={formData.firstName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.firstName && !!fieldErrors.firstName}
        helperText={fieldTouched.firstName && fieldErrors.firstName}
        sx={{ mb: 2 }}
      />
      <TextField
        className="userInput"
        label="Last Name"
        name="lastName"
        fullWidth
        required
        value={formData.lastName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.lastName && !!fieldErrors.lastName}
        helperText={fieldTouched.lastName && fieldErrors.lastName}
        sx={{ mb: 2 }}
      />
      <TextField
        className="userInput"
        label="Email"
        name="email"
        type="email"
        fullWidth
        required
        value={formData.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.email && !!fieldErrors.email}
        helperText={
          formData.inviteToken
            ? "📧 Email verified via invitation"
            : fieldTouched.email && fieldErrors.email
        }
        disabled={!!formData.inviteToken}
        sx={{ mb: 2 }}
      />
      <TextField
        className="userInput"
        label="Phone Number"
        name="phone"
        type="tel"
        fullWidth
        required
        value={formData.phone}
        onChange={handleChange}
        onBlur={handleBlur}
        error={fieldTouched.phone && !!fieldErrors.phone}
        helperText={fieldTouched.phone && fieldErrors.phone}
        sx={{ mb: 2 }}
      />
      <TextField
        className="userInput"
        label="Password"
        name="password"
        type={showPassword ? "text" : "password"}
        value={formData.password}
        onChange={handleChange}
        onBlur={handleBlur}
        fullWidth
        required
        error={fieldTouched.password && !!fieldErrors.password}
        helperText={fieldTouched.password && fieldErrors.password}
        sx={{ mb: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={togglePasswordVisibility} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Live Progress Bar */}
      <Box sx={{ mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={passwordStrength}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: "#f0f0f0",
            "& .MuiLinearProgress-bar": {
              backgroundColor:
                passwordStrength < 50
                  ? "#f44336"
                  : passwordStrength < 75
                  ? "#ff9800"
                  : "#4caf50",
            },
          }}
        />
      </Box>

      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
      >
        Password must:
        <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
          <li
            style={{ color: formData.password.length >= 8 ? "green" : "red" }}
          >
            Be at least 8 characters
          </li>
          <li
            style={{ color: /[0-9]/.test(formData.password) ? "green" : "red" }}
          >
            Contain a number
          </li>
          <li
            style={{
              color: /[!@#$%^&*()_\-+=]/.test(formData.password)
                ? "green"
                : "red",
            }}
          >
            Contain a special character: ! @ # $ % ^ & * ( ) _ - + =
          </li>
          <li
            style={{ color: /[A-Z]/.test(formData.password) ? "green" : "red" }}
          >
            Contain an uppercase letter
          </li>
        </ul>
      </Typography>
    </>,

    <>
      {console.log(
        "🎯 Step 2 Render - formData.inviteToken:",
        formData.inviteToken
      )}
      {/* Shop Selection Section */}
      <Box sx={{ mt: 2 }}>
        {inviteShop ? (
          <>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#eff6ff",
              borderRadius: "8px",
              border: "1px solid #3b82f6",
              mb: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#1e40af", mb: 1 }}
            >
              🎉 You've been invited to join:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {inviteShop.shop_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[inviteShop.address, inviteShop.city, inviteShop.state]
                .filter(Boolean)
                .join(", ")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Complete registration to join this shop
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                component={RouterLink}
                to="/privacypolicy"
                target="_blank"
                variant="contained"
                sx={{ my: 1, textAlign: "center" }}
              >
                Privacy Policy
              </Button>
              <Button
                component={RouterLink}
                to="/tos"
                variant="contained"
                target="_blank"
                sx={{ my: 1, textAlign: "center" }}
              >
                Terms of Service
              </Button>
            </Box>
            <Typography
              variant="body"
              sx={{ my: 2, maxWidth: 300, textAlign: "center" }}
            >
              <Checkbox
                required
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              By ticking this box I agree that I have read the privacy policy
              and terms of service.
            </Typography>
          </Box>
          </>
        ) : formData.inviteToken ? (
          // Existing invite flow (from props)
          <Box sx={{ mt: 2, mb: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                📧 You've Been Invited!
              </Typography>
              <Typography variant="body2">
                You're signing up as a mechanic through an invitation. Your
                account will need to be approved by the shop owner before you
                can access the full system.
              </Typography>
            </Alert>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 3,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  component={RouterLink}
                  to="/privacypolicy"
                  target="_blank"
                  variant="contained"
                  sx={{ my: 1, textAlign: "center" }}
                >
                  Privacy Policy
                </Button>
                <Button
                  component={RouterLink}
                  to="/tos"
                  variant="contained"
                  target="_blank"
                  sx={{ my: 1, textAlign: "center" }}
                >
                  Terms of Service
                </Button>
              </Box>
              <Typography
                variant="body"
                sx={{ my: 2, maxWidth: 300, textAlign: "center" }}
              >
                <Checkbox
                  required
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                />
                By ticking this box I agree that I have read the privacy policy
                and terms of service.
              </Typography>
            </Box>
          </Box>
        ) : (
          // ✅ Normal shop search (no invite) - ORIGINAL UI
          <>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Select Shop to Claim:
            </Typography>

            {/* Shop Name Field (Optional) */}
            <TextField
              fullWidth
              label="Shop Name (Optional)"
              placeholder="e.g., Joe's Auto Repair"
              value={formData.shopName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, shopName: e.target.value }))
              }
              sx={{ mb: 2 }}
              helperText="Leave blank to see all shops in your ZIP code"
            />

            {/* ZIP Code Field (Required) */}
            <TextField
              fullWidth
              label="ZIP Code *"
              placeholder="63049"
              value={formData.shopZipcode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 5);
                setFormData((prev) => ({ ...prev, shopZipcode: value }));
              }}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 5 }}
              helperText="Enter 5-digit ZIP code to search"
            />

            {/* Search Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearchShops}
              disabled={
                !formData.shopZipcode || formData.shopZipcode.length !== 5
              }
              sx={{
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                backgroundColor: "#2563eb",
                "&:hover": { backgroundColor: "#1d4ed8" },
                mb: 3,
              }}
            >
              {isBroadSearching ? (
                <>
                  <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                  Searching...
                </>
              ) : (
                "Search for Shop"
              )}
            </Button>

            {/* Search Results - Hide when manual entry is enabled */}
            {hasSearched && !allowManualEntry && (
              <Box sx={{ mt: 3 }}>
                {availableShops.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No shops found. You can manually enter your shop information
                    below.
                  </Alert>
                ) : (
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      Select Your Shop ({availableShops.length} found):
                    </Typography>
                    <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                      {availableShops.map((shop) => {
                        const isSelected =
                          formData.selectedPlaceId === shop.place_id;
                        const isClaimed = shop.in_network; // Shop is claimed if in_network is true

                        return (
                          <Card
                            key={shop.place_id}
                            sx={{
                              mb: 2,
                              border: isSelected
                                ? "2px solid #2563eb"
                                : "1px solid #e5e7eb",
                              backgroundColor: isSelected
                                ? "#eff6ff"
                                : isClaimed
                                ? "#f9fafb"
                                : "white",
                              opacity: isClaimed ? 0.6 : 1,
                            }}
                          >
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {shop.shop_name}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {shop.address ||
                                  `${shop.street_address}, ${shop.city}, ${shop.state} ${shop.zip_code}`}
                              </Typography>

                              {shop.distance_miles && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    mt: 0.5,
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  📍 {shop.distance_miles} miles away
                                </Typography>
                              )}

                              {shop.phone_number && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  📞 {shop.phone_number}
                                </Typography>
                              )}

                              {isClaimed && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{
                                    display: "block",
                                    mt: 1,
                                    fontWeight: 600,
                                  }}
                                >
                                  ⚠️ This shop has already been claimed by
                                  another owner
                                </Typography>
                              )}

                              {/* Action Button */}
                              <Button
                                variant={
                                  isSelected
                                    ? "contained"
                                    : isClaimed
                                    ? "contained"
                                    : "outlined"
                                }
                                color={
                                  isSelected
                                    ? "primary"
                                    : isClaimed
                                    ? "error"
                                    : "inherit"
                                }
                                fullWidth
                                sx={{
                                  mt: 2,
                                  py: 1.5,
                                  fontWeight: 600,
                                  ...(isClaimed && {
                                    backgroundColor: "#d32f2f",
                                    color: "white",
                                    "&:hover": {
                                      backgroundColor: "#d32f2f",
                                    },
                                    "&.Mui-disabled": {
                                      backgroundColor: "#d32f2f",
                                      color: "white",
                                      opacity: 0.8,
                                    },
                                  }),
                                }}
                                onClick={() => handleClaimShop(shop)}
                                disabled={isClaimed}
                              >
                                {isClaimed
                                  ? "ALREADY CLAIMED"
                                  : isSelected
                                  ? "Shop Claimed ✓"
                                  : "Claim Shop"}
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Search Again / Manual Entry Options - Show after search results */}
            {hasSearched && !isBroadSearching && !allowManualEntry && (
              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                  textAlign: "center",
                  border: "1px dashed #ccc",
                }}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {availableShops.length === 0
                    ? "⚠️ We couldn't find your shop. Please try searching again."
                    : "Don't see your shop in the results above?"}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Search Again Button */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Reset search state to allow searching again
                      setHasSearched(false);
                      setAvailableShops([]);
                      // Clear shop name and zip code to allow fresh input
                      setFormData((prev) => ({
                        ...prev,
                        shopName: "",
                        shopZipcode: "",
                        joiningShopId: "",
                        selectedPlaceId: "",
                      }));
                    }}
                    sx={{
                      fontWeight: 600,
                      py: 1.5,
                      px: 3,
                      backgroundColor: "#2563eb",
                      "&:hover": { backgroundColor: "#1d4ed8" },
                    }}
                  >
                    🔍 SEARCH AGAIN
                  </Button>

                  {/* Enter Manually Button */}
                  {/*<Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setAllowManualEntry(true);
                      setFormData((prev) => ({
                        ...prev,
                        manuallyEntered: true,
                      }));
                    }}
                    sx={{
                      fontWeight: 600,
                      py: 1.5,
                      px: 3,
                      borderColor: "#2563eb",
                      color: "#2563eb",
                      "&:hover": {
                        borderColor: "#1d4ed8",
                        backgroundColor: "rgba(37, 99, 235, 0.04)",
                      },
                    }}
                    >
                      ✍️ ENTER SHOP DETAILS MANUALLY
                    </Button>*/}
                  </Box>
                </Box>
            )}
          </>
        )}

        {/* Only show manual entry fields after a shop is claimed OR manual entry is allowed */}
        {(allowManualEntry) && (
          //{(formData.joiningShopId || allowManualEntry) && (
          <Box sx={{ mt: 3 }}>
            {formData.joiningShopId && !allowManualEntry && (
              <Typography
                variant="body2"
                color="primary"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                ✅ Shop details auto-populated from Google. You can edit or add
                missing information:
              </Typography>
            )}
            {allowManualEntry && (
              <Typography
                variant="body2"
                color="warning.main"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                ⚠️ Manual Entry: Please enter your shop details carefully. Admin
                will verify before approval.
              </Typography>
            )}

            <TextField
              className="userInput"
              label="Shop Name"
              name="shopName"
              fullWidth
              value={formData.shopName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={fieldTouched.shopName && !!fieldErrors.shopName}
              helperText={
                (fieldTouched.shopName && fieldErrors.shopName) ||
                "Auto-filled from Google"
              }
              sx={{ mt: 2 }}
            />
            <TextField
              className="userInput"
              label="Shop Phone"
              name="shopPhone"
              fullWidth
              value={formData.shopPhone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={fieldTouched.shopPhone && !!fieldErrors.shopPhone}
              helperText={
                (fieldTouched.shopPhone && fieldErrors.shopPhone) ||
                "Auto-filled from Google (if available)"
              }
              sx={{ mt: 2 }}
            />
            <TextField
              className="userInput"
              label="Shop Website"
              name="shopSite"
              fullWidth
              value={formData.shopSite}
              onChange={handleChange}
              onBlur={handleBlur}
              error={fieldTouched.shopSite && !!fieldErrors.shopSite}
              helperText={
                (fieldTouched.shopSite && fieldErrors.shopSite) ||
                "Auto-filled from Google (if available)"
              }
              placeholder="e.g., yourshop.com or www.yourshop.com"
              sx={{ mt: 2 }}
            />
            <TextField
              className="userInput"
              label="Street Address"
              name="shopAddress"
              fullWidth
              value={formData.shopAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              error={fieldTouched.shopAddress && !!fieldErrors.shopAddress}
              helperText={
                (fieldTouched.shopAddress && fieldErrors.shopAddress) ||
                "Auto-filled from Google"
              }
              sx={{ mt: 2 }}
            />
            <TextField
              className="userInput"
              label="City"
              name="city"
              fullWidth
              value={formData.city}
              onChange={handleChange}
              onBlur={handleBlur}
              error={fieldTouched.city && !!fieldErrors.city}
              helperText={
                (fieldTouched.city && fieldErrors.city) ||
                "Auto-filled from Google"
              }
              sx={{ mt: 2 }}
            />
            <TextField
              className="userInput"
              select
              label="State"
              name="state"
              fullWidth
              required
              value={formData.state}
              onChange={handleChange}
              onBlur={handleBlur}
              error={fieldTouched.state && !!fieldErrors.state}
              helperText={
                (fieldTouched.state && fieldErrors.state) ||
                "Auto-filled from Google"
              }
              sx={{ mt: 2 }}
            >
              {usStates.map(({ code, name }) => (
                <MenuItem key={code} value={code}>
                  {name} ({code})
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* Privacy Policy and Terms Agreement - Show in step 0 for normal signup */}
        {!formData.inviteToken && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 4,
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button
                component={RouterLink}
                to="/privacypolicy"
                target="_blank"
                variant="contained"
                sx={{
                  textAlign: "center",
                  backgroundColor: "#2563eb",
                  "&:hover": { backgroundColor: "#1d4ed8" },
                }}
              >
                Privacy Policy
              </Button>
              <Button
                component={RouterLink}
                to="/tos"
                variant="contained"
                target="_blank"
                sx={{
                  textAlign: "center",
                  backgroundColor: "#2563eb",
                  "&:hover": { backgroundColor: "#1d4ed8" },
                }}
              >
                Terms of Service
              </Button>
            </Box>

            <Typography
              variant="body2"
              sx={{
                maxWidth: 400,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Checkbox
                required
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                sx={{ mr: 1 }}
              />
              By ticking this box I agree that I have read the privacy policy
              and terms of service.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Mechanic invite: join the inviting shop. Do not show claim/search. */}
      {false && formData.inviteToken && !inviteTokenFromURL && (
        <Box sx={{ mt: 2, mb: 3 }}>
          <>
            <Typography
              variant="h6"
              color="#e63926"
              textAlign={"center"}
              sx={{ mb: 3 }}
            >
              Find your shop to claim it!
            </Typography>

            {/* Adam's New Simple Search UI */}
            {!hasSearched && (
              <Box className="userInput" sx={{ mt: 2 }}>
                <TextField
                  label="Shop Name (Optional)"
                  name="shopName"
                  fullWidth
                  value={formData.shopName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={fieldTouched.shopName && !!fieldErrors.shopName}
                  helperText={
                    (fieldTouched.shopName && fieldErrors.shopName) ||
                    "Optional - Leave blank to search by ZIP code only"
                  }
                  placeholder="Enter your shop name (optional)"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="ZIP Code"
                  name="shopZipcode"
                  fullWidth
                  required
                  value={formData.shopZipcode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={fieldTouched.shopZipcode && !!fieldErrors.shopZipcode}
                  helperText={
                    fieldTouched.shopZipcode && fieldErrors.shopZipcode
                  }
                  placeholder="Enter your ZIP code"
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleSearchShops}
                  disabled={
                    !formData.shopZipcode || formData.shopZipcode.length !== 5
                  }
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  }}
                >
                  🔍 Search for My Shop
                </Button>
              </Box>
            )}

            {/* Loading State */}
            {isBroadSearching && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={50} sx={{ mb: 2 }} />
                <Typography variant="h6" color="primary" fontWeight="bold">
                  🔍 Searching for your Auto Shop...
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  We're checking our database and Google Places
                </Typography>
              </Box>
            )}

            {/* Results Display */}
            {availableShops.length > 0 && !isBroadSearching && (
              <Box
                sx={{
                  mt: 2,
                  maxHeight: 300,
                  overflowY: "auto",
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  p: 1,
                }}
              >
                {availableShops.map((shop) => {
                  const isSelected = formData.selectedPlaceId === shop.place_id;
                  const isClaimed = shop.in_network === true;
                  return (
                    <Card
                      key={shop.place_id}
                      variant="outlined"
                      sx={{
                        mb: 2,
                        borderColor: isSelected ? "primary.main" : "grey.300",
                        backgroundColor: "white",
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {shop.shop_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {shop.address}
                        </Typography>
                        {shop.distance_miles && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              fontSize: "0.75rem",
                            }}
                          >
                            📍 {shop.distance_miles} miles away
                          </Typography>
                        )}
                        {shop.phone_number && (
                          <Typography variant="body2" color="text.secondary">
                            📞 {shop.phone_number}
                          </Typography>
                        )}
                        {shop.website && (
                          <Typography variant="body2" color="text.secondary">
                            🌐 {shop.website}
                          </Typography>
                        )}
                        {isClaimed && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 1, fontWeight: 600 }}
                          >
                            ⚠️ This shop has already been claimed by another
                            owner
                          </Typography>
                        )}
                        <Button
                          variant={
                            isSelected
                              ? "contained"
                              : isClaimed
                              ? "contained"
                              : "outlined"
                          }
                          color={
                            isSelected
                              ? "primary"
                              : isClaimed
                              ? "error"
                              : "inherit"
                          }
                          fullWidth
                          sx={{
                            mt: 1,
                            ...(isClaimed && {
                              backgroundColor: "#d32f2f",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#d32f2f",
                              },
                              "&.Mui-disabled": {
                                backgroundColor: "#d32f2f",
                                color: "white",
                                opacity: 0.8,
                              },
                            }),
                          }}
                          onClick={() => handleClaimShop(shop)}
                          disabled={isClaimed}
                        >
                          {isClaimed
                            ? "ALREADY CLAIMED"
                            : isSelected
                            ? "Shop Claimed"
                            : "Claim Shop"}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}

            {/* Search Again / Manual Entry Options - Show after search results */}
            {hasSearched && !isBroadSearching && !allowManualEntry && (
              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                  textAlign: "center",
                  border: "1px dashed #ccc",
                }}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {availableShops.length === 0
                    ? "⚠️ We couldn't find your shop. Please try searching again."
                    : "Don't see your shop in the results above?"}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      // Reset search state to allow searching again
                      setHasSearched(false);
                      setAvailableShops([]);
                      // Clear shop name and zip code to allow fresh input
                      setFormData((prev) => ({
                        ...prev,
                        shopName: "",
                        shopZipcode: "",
                        joiningShopId: "",
                        selectedPlaceId: "",
                      }));
                    }}
                    sx={{
                      fontWeight: 600,
                      py: 1.5,
                      px: 3,
                    }}
                  >
                    🔍 Search Again
                  </Button>
                  {/*<Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setAllowManualEntry(true);
                      setFormData((prev) => ({
                        ...prev,
                        manuallyEntered: true,
                      }));
                    }}
                    sx={{
                      fontWeight: 600,
                      py: 1.5,
                      px: 3,
                    }}
                  >
                    ✍️ Enter Shop Details Manually
                  </Button>*/}
                </Box>
              </Box>
            )}

            {/* Only show manual entry fields after a shop is claimed OR manual entry is allowed */}
            {(allowManualEntry) && (
              //{(formData.joiningShopId || allowManualEntry) && (
              <Box sx={{ mt: 3 }}>
                {formData.joiningShopId && !allowManualEntry && (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    ✅ Shop details auto-populated from Google. You can edit or
                    add missing information:
                  </Typography>
                )}
                {allowManualEntry && (
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    ⚠️ Manual Entry: Please enter your shop details carefully.
                    Admin will verify before approval.
                  </Typography>
                )}

                <TextField
                  className="userInput"
                  label="Shop Name"
                  name="shopName"
                  fullWidth
                  value={formData.shopName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={fieldTouched.shopName && !!fieldErrors.shopName}
                  helperText={
                    (fieldTouched.shopName && fieldErrors.shopName) ||
                    "Auto-filled from Google"
                  }
                  sx={{ mt: 2 }}
                />
                <TextField
                  className="userInput"
                  label="Shop Phone"
                  name="shopPhone"
                  fullWidth
                  value={formData.shopPhone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={fieldTouched.shopPhone && !!fieldErrors.shopPhone}
                  helperText={
                    (fieldTouched.shopPhone && fieldErrors.shopPhone) ||
                    "Auto-filled from Google (if available)"
                  }
                  sx={{ mt: 2 }}
                />
                <TextField
                  className="userInput"
                  label="Shop Website"
                  name="shopSite"
                  fullWidth
                  value={formData.shopSite}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={fieldTouched.shopSite && !!fieldErrors.shopSite}
                  helperText={
                    (fieldTouched.shopSite && fieldErrors.shopSite) ||
                    "Auto-filled from Google (if available)"
                  }
                  placeholder="e.g., yourshop.com or www.yourshop.com"
                  sx={{ mt: 2 }}
                />
                <TextField
                  className="userInput"
                  label="Street Address"
                  name="shopAddress"
                  fullWidth
                  value={formData.shopAddress}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={fieldTouched.shopAddress && !!fieldErrors.shopAddress}
                  helperText={
                    (fieldTouched.shopAddress && fieldErrors.shopAddress) ||
                    "Auto-filled from Google"
                  }
                  sx={{ mt: 2 }}
                />
                <TextField
                  className="userInput"
                  label="City"
                  name="city"
                  fullWidth
                  value={formData.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={fieldTouched.city && !!fieldErrors.city}
                  helperText={
                    (fieldTouched.city && fieldErrors.city) ||
                    "Auto-filled from Google"
                  }
                  sx={{ mt: 2 }}
                />
                <TextField
                  className="userInput"
                  select
                  label="State"
                  name="state"
                  fullWidth
                  required
                  value={formData.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={fieldTouched.state && !!fieldErrors.state}
                  helperText={
                    (fieldTouched.state && fieldErrors.state) ||
                    "Auto-filled from Google"
                  }
                  sx={{ mt: 2 }}
                >
                  {usStates.map(({ code, name }) => (
                    <MenuItem key={code} value={code}>
                      {name} ({code})
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  component={RouterLink}
                  to="/privacypolicy"
                  target="_blank"
                  variant="contained"
                  sx={{ my: 1, textAlign: "center" }}
                >
                  Privacy Policy
                </Button>
                <Button
                  component={RouterLink}
                  to="/tos"
                  variant="contained"
                  target="_blank"
                  sx={{ my: 1, textAlign: "center" }}
                >
                  Terms of Service
                </Button>
              </Box>
              <Typography
                variant="body"
                sx={{ my: 2, maxWidth: 300, textAlign: "center" }}
              >
                <Checkbox
                  required
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                />
                By ticking this box I agree that I have read the privacy policy
                and terms of service.
              </Typography>
            </Box>
          </>
        </Box>
      )}

      {/* Bot protection — all owner/mechanic signup paths (normal claim, invite URL, invite props) */}
      {!formData.inviteToken && formData.role === "Owner" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          After your shop is approved, you will add a credit card on file to submit
          quotes. You can stay on the Free plan — the card is for future platform
          fees only (no subscription required).
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}>
        <TurnstileWidget
          ref={turnstileRef}
          onVerify={handleTurnstileVerify}
          onExpire={handleTurnstileExpire}
          onError={() => setTurnstileToken(null)}
        />
      </Box>
    </>,
  ];

  return (
    <>
      <Typography
        variant="h5"
        mb={3}
        fontWeight={800}
        borderBottom={3}
        textAlign="center"
      >
        Auto Shop Sign Up
      </Typography>
      <form onSubmit={handleSubmit}>
        {inputArray[step]}
        <Box
          fullWidth
          sx={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            minHeight: "80px",
          }}
        >
          {step < inputArray.length - 1 && (
            <Button
              variant="contained"
              onClick={next}
              disabled={!isFormValid || isSubmitting}
              sx={{ position: "absolute", right: 0 }}
            >
              Next
            </Button>
          )}

          {step > 0 && (
            <Button
              variant="contained"
              onClick={prev}
              sx={{ position: "absolute", left: 0 }}
            >
              Back
            </Button>
          )}

          {step === inputArray.length - 1 && (
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid || isSubmitting}
              sx={{ position: "absolute", right: 0 }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Signing Up...
                </>
              ) : (
                "Sign Up"
              )}
            </Button>
          )}
        </Box>
      </form>

      {/* Upfront Shop Owner Acknowledgement Modal */}
      <Dialog
        open={showOwnershipAcknowledgement}
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle
          sx={{
            bgcolor: "#fff3cd",
            color: "#856404",
            borderBottom: "2px solid #ffc107",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            ⚠️ Shop Owner Registration Only
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography variant="body1" fontWeight="600" gutterBottom>
            This registration is for shop owners and business owners only.
          </Typography>

          <Typography variant="body2" paragraph sx={{ mt: 2, mb: 2 }}>
            By proceeding, you certify that you are the legal owner or
            authorized representative of the auto shop you will be claiming.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="600" gutterBottom>
              Are you a mechanic working at someone else's shop?
            </Typography>
            <Typography variant="body2">
              Please ask your shop owner to send you an invitation instead. This
              will give you access to manage your shop's account.
            </Typography>
          </Alert>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> False ownership claims may result in
              account suspension.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, justifyContent: "center" }}>
          <Button
            onClick={handleOwnershipDecline}
            variant="outlined"
            color="inherit"
            size="large"
          >
            Cancel
          </Button>
          <Button
            onClick={handleOwnershipAcknowledge}
            variant="contained"
            color="primary"
            size="large"
            sx={{ fontWeight: 600, px: 3 }}
          >
            I'm the Owner - Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MechanicSignupForm;
